import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';

import { NotifyButtonContext } from '../../../buttons/notify-button/contexts/NotifyButtonContext.js';
import { NotificationQueryClientContext } from './notification-query-client.js';
import {
  notificationGenericErrorToast,
  notificationLimitReachedToast,
  noWritePermissionToast,
} from './toast-alerts.js';

/** @internal */
export interface UseNotificationClientReturn {
  toggleNotifications: () => Promise<void>;
  enabled: boolean;
  loading: boolean;
  hasNotFoundError: boolean;
  hasReadPermission: boolean;
  hasWritePermission: boolean;
}

/**
 * Base client shared for all notifications services.
 *
 * @internal
 * */
export function useNotificationClient<
  NotificationConfigType,
  NotificationType extends { id: string },
  PaginatedNotificationType extends {
    count: number;
    results: Array<NotificationType>;
  },
  ErrorEnvelopeError extends {
    body: {
      error: {
        details?: {
          errorCode?: string;
        };
      };
    };
  },
>(
  notificationType: 'event' | 'resource' | 'self',
  config: NotificationConfigType,
  connectors: {
    list: (
      config: NotificationConfigType,
      abortSignal: AbortSignal,
    ) => Promise<PaginatedNotificationType>;
    create: (config: NotificationConfigType) => Promise<NotificationType>;
    destroy: (id: string) => Promise<void>;
    isErrorEnvelopeError: (e: unknown) => e is ErrorEnvelopeError;
  },
): UseNotificationClientReturn {
  const intl = useIntl();

  // Using the standard context providing the query client.
  const notificationQueryClient = useContext(NotificationQueryClientContext);
  // Using the NotifyButton context. If the hook is used within a notifybutton
  // it needs to be able to close the overlay.
  const { closeOverlay } = useContext(NotifyButtonContext);

  // Standard fetch query to get the currently active
  // notifications for the given resource.
  const {
    data: notificationData,
    error: notificationListError,
    isLoading: notificationIsLoading,
  } = useQuery(
    {
      initialData: null,
      queryKey: [config, notificationType],
      queryFn: async ({ signal }) => {
        const data = await connectors.list(config, signal);

        // Pick the first notification in the result list.
        const firstResult = data.results.at(0);
        if (firstResult) {
          return firstResult;
        }

        return null;
      },
    },
    notificationQueryClient,
  );

  // Standard create notification mutation to call the creation part of the service.
  const {
    mutate: createNotification,
    error: notificationCreateError,
    isPending: isCreatePending,
  } = useMutation(
    {
      mutationKey: [config, notificationType, 'create'],
      mutationFn: async () => {
        return await connectors.create(config);
      },
      throwOnError: false,
      // Handle various error scenarios to prompt user information.
      onError: (error: Error) => {
        if (error.name === '403') {
          noWritePermissionToast(intl, notificationData !== null);
          closeOverlay?.();
        } else if (
          error.name === '400' &&
          connectors.isErrorEnvelopeError(error) &&
          error.body.error.details?.errorCode?.includes(
            'NotificationLimitReached',
          )
        ) {
          notificationLimitReachedToast(intl, notificationType);
          closeOverlay?.();
        } else {
          notificationGenericErrorToast(intl);
        }
      },
      // Update the query cache to avoid the necessary refetch. The create service
      // already responds with the created notification, so we can use that one in the
      // state.
      onSuccess: (data: NotificationType) => {
        notificationQueryClient.setQueryData([config, notificationType], data);
      },
    },
    notificationQueryClient,
  );

  // Standard delete notification mutation to call the destroy part of the service.
  const {
    mutate: deleteNotification,
    error: notificationDeleteError,
    isPending: isDeletePending,
  } = useMutation(
    {
      mutationKey: [config, notificationType, 'delete'],
      mutationFn: async () => {
        if (notificationData?.id) {
          return connectors.destroy(notificationData.id);
        }
      },
      throwOnError: false,
      onSuccess: (data: void) => {
        // Update the query cache to avoid the necessary refetch. When successful,
        // we can assume that the notification actually got destroyed.
        notificationQueryClient.setQueryData([config, notificationType], null);
      },
      onError: (error: Error) => {
        if (error.name === '403') {
          noWritePermissionToast(intl, notificationData !== null);
          closeOverlay?.();
        }
      },
    },
    notificationQueryClient,
  );

  const toggleNotifications = useCallback(async () => {
    closeOverlay?.();
    if (notificationData?.id) {
      deleteNotification();
    } else {
      createNotification();
    }
  }, [
    closeOverlay,
    createNotification,
    deleteNotification,
    notificationData?.id,
  ]);

  return {
    toggleNotifications,
    enabled: notificationData !== null,
    loading: notificationIsLoading || isCreatePending || isDeletePending,
    hasNotFoundError: notificationListError?.name === '404',
    hasReadPermission: notificationListError?.name !== '403',
    hasWritePermission:
      notificationCreateError?.name !== '403' &&
      notificationDeleteError?.name !== '403',
  };
}
